<template>
  <div>
    <Grid
      :headers="headers"
      :payload="{results, count, next, previous}"
      @api="getDevices()"
      @filter="showFilterModal = true"
      @clearFilters="$refs.desktopFilters.reset()"
    >
      <template #buttons>
        <!-- Batch devices geolocation update -->

        <!-- Geo update actions -->
        <base-dropdown-2
          :loading="isDownloading"
          :disabled="isDownloading"
          size="sm"
          placement="bottom"
        >
          <template>
            <base-icon icon="map-pin" class="text-xl mr-2" />
            {{ $t(`mygwf.components.Button.device_update_geolocation`) }}</template
          >
          <template #body>
            <base-button
              size="sm"
              action=""
              context="outline"
              class="w-full"
              :loading="pendingBatchGeoRequest"
              @click="$refs.fileInput.click()"
            >
              {{ $t('mygwf.components.Button.device_batch_update') }}
            </base-button>
            <base-button
              @click="downloadTemplate"
              :loading="pendingRequest"
              size="sm"
              context="outline"
              class="w-full border-b"
            >
              {{ $t('mygwf.components.Button.device_download_template') }}
            </base-button>
          </template>
        </base-dropdown-2>

        <!-- Create / Add devices to group -->
        <a-popover
          :visible="addToGroup.showComponent && checked.length > 0"
          placement="bottomRight"
          :visibleChange="onVisibleChange()"
        >
          <template #content>
            <!-- Close popover -->
            <div class="flex justify-end mb-2">
              <base-icon
                icon="close"
                class="cursor-pointer text-gray-500"
                @click="addToGroup.showComponent = false"
              />
            </div>

            <!-- Action buttons -->
            <div class="flex flex-col space-y-2">
              <!-- Create new group -->
              <base-button
                size="sm"
                action="create_device_group"
                @click="
                  addToGroup.showComponent = false
                  addToGroup.showForm = false
                  createGroup()
                "
              >
                <base-icon icon="group" class="text-lg mr-2" />
              </base-button>

              <!-- Add to existing group -->
              <base-button
                size="sm"
                action=""
                @click="
                  addToGroup.showForm = true
                  fetchDeviceGroupsByCompanyId()
                "
              >
                <base-icon icon="group" class="text-lg mr-2" />
                {{ $t('mygwf.components.Button.device_add_to_existing_group') }}
              </base-button>

              <!-- Group selection -->
              <div v-if="addToGroup.showForm" class="flex flex-col">
                <fieldset>
                  <label class="mb-0.5 block text-secondary-dark text-sm">{{
                    $t(`mygwf.components.Label.group_name`)
                  }}</label>
                  <base-select
                    ref="companySelect"
                    v-model="addToGroup.selectedGroupUuid"
                    :options="deviceGroups"
                    :optionType="{id: 'uuid', label: 'name'}"
                    allowClear
                  />
                </fieldset>
                <base-button
                  class="mt-3 self-end"
                  size="sm"
                  action=""
                  :loading="pendingRequest"
                  @click="onAddToExistingGroup"
                >
                  {{ $t('mygwf.components.Button.save') }}
                </base-button>
              </div>
            </div>
          </template>

          <base-button
            size="sm"
            action=""
            :disabled="checked.length < 1"
            @click="addToGroup.showComponent = true"
          >
            <base-icon icon="group" class="text-lg mr-2" />
            {{ $t('mygwf.components.Button.device_add_to_group') }}
          </base-button>
        </a-popover>

        <!-- Create device -->
        <base-button size="sm" action="create_device" @click="createDevice">
          <base-icon icon="file-plus" class="text-lg" />
        </base-button>
      </template>

      <template #filter>
        <DevicesFilter ref="desktopFilters" />
      </template>

      <template v-if="sameCompany" #[`head.check`]>
        <base-check v-model="checkAll" />
      </template>

      <template #[`body.check`]="{item}">
        <base-check v-model="checked" :value="item" :disabled="disabledCheck(item)" />
      </template>

      <template #[`body.meter__content_type__model`]="{item}">
        <div class="flex flex-col">
          <span>{{ item.meter_type }} </span>
          <span class="text-gray-400">{{ item.meter_serial }}</span>
        </div>
      </template>

      <template #[`body.measuring_point__name`]="{item}">
        <div class="flex items-center space-x-5">
          <base-button-icon
            size="sm"
            icon="map-pin"
            class="bg-secondary rounded h-7 w-7 text-gray-800 hover:text-black hover:bg-white"
            :tooltip="$t(`mygwf.components.Label.measuring_point`)"
            @click="mpClick(item)"
          />
          <span class="truncate">{{ item.mp_name ? item.mp_name : '-' }} {{ showMP }} </span>
        </div>
      </template>

      <template #[`body.last_communication_device__content_type__model`]="{item}">
        <div v-if="item.comd_type" class="flex items-center max-w-fit space-x-5">
          <a-popover
            overlayClassName="no-padding"
            :visible="clickedKey === item.uuid"
            @visibleChange="e => (clickedKey = e ? item.uuid : '')"
            placement="rightTop"
            trigger="click"
          >
            <template slot="content">
              <ul v-for="(value, key) in keys" :key="key">
                <li class="">
                  <div
                    class="flex items-center space-x-3 border-b border-white p-3 pr-4 bg-secondary-light"
                  >
                    <div class="flex flex-col">
                      <span class="text-sm">{{ value }}</span>
                      <span class="text-xs text-gray-400">{{ text(key) }} </span>
                    </div>
                  </div>
                </li>
              </ul>
            </template>
            <base-button-icon
              size="sm"
              icon="key"
              :disabled="keysLoading.length > 0"
              :loading="item.uuid === keysLoading"
              tooltip="key_info"
              class="bg-blue-500 rounded text-white h-6 hover:text-blue-500 hover:bg-white hover:shadow"
              @click="deviceKeys(item)"
            />
          </a-popover>
          <div class="flex flex-col">
            <span>{{ item.comd_type }} </span>
            <span class="text-gray-400">{{ item.comd_serial }} </span>
          </div>
        </div>
        <div v-else class="text-center min-w-full">-</div>
      </template>

      <template #[`body.value`]="{item}">
        <div class="flex space-x-5 items-center">
          <span class="w-1/2">{{ item.state.volume }} m<sup>3</sup></span>
          <base-button-icon
            icon="report"
            size="sm"
            :tooltip="$t(`mygwf.components.Label.volume_reports`)"
            @click="volumeClick(item)"
          />
        </div>
      </template>

      <template #[`body.alerts`]="{item}">
        <div class="w-1/2">
          <!-- ALERTS POPOVER -->
          <a-popover
            overlayClassName="no-padding"
            :visible="clickedAlert === item.uuid"
            @visibleChange="e => (clickedAlert = e ? item.uuid : '')"
            placement="rightTop"
            trigger="click"
          >
            <template slot="content">
              <div v-if="item.state.alertCount > 0">
                <ul v-for="(value, key) in deviceAlerts(item)" :key="key">
                  <li v-if="value" class="">
                    <div class="flex items-center space-x-3 border-b border-white p-2 pr-3">
                      <base-icon icon="info" class="text-orange-light text-lg" />
                      <span class="text-sm">{{ alertText(key) }} </span>
                    </div>
                  </li>
                </ul>
              </div>
              <div v-else class="flex items-center space-x-2 border-b border-white p-2">
                <base-icon icon="success" class="text-green-light text-lg" />
                <span class="text-sm">{{ $t(`mygwf.components.Label.no_alerts`) }} </span>
              </div>
            </template>
            <div>
              <base-button-icon
                :icon="item.state.alertCount === 0 ? 'success' : 'info'"
                size="sm"
                :tooltip="
                  item.state.alertCount > 0
                    ? `${item.state.alertCount} ${$t(`mygwf.components.Label.alerts`)}`
                    : `${$t(`mygwf.components.Label.no_alerts`)}`
                "
                :class="[
                  'text-xl m-auto',
                  item.state.alertCount === 0 ? 'text-green-light' : 'text-orange-light',
                ]"
              />
            </div>
          </a-popover>
        </div>
      </template>

      <template #[`body.last_entry`]="{item}">
        <span>{{ $d(new Date(item.state.last_entry), 'long') }} </span>
      </template>

      <template #[`body.groups`]="{item}">
        <ul v-if="item.groups.length" class="grid grid-flow-row grid-cols-1 gap-y-1.5 p-1">
          <li
            v-for="group in item.groups"
            :key="group"
            class="rounded cursor-pointer truncate border px-2 leading-tight bg-secondary max-w-min hover:bg-white hover:shadow"
            @click="groupClick(group)"
          >
            <span class="text-xs">{{ group.name }}</span>
          </li>
        </ul>
        <div v-else class="">-</div>
      </template>

      <template #alert="{fetching}">
        <base-alert
          v-if="!fetching && showLoadAll"
          :link="$store.getters['app/queryAll']"
          type="info"
          :text="{
            msg: 'get_all_results',
            part: `${results.length} ${$store.getters['app/pageTitle']}`,
          }"
          size="sm"
        />
      </template>
    </Grid>
    <DevicesMesuringPoint
      v-if="mpDrawer"
      :visible="mpDrawer"
      :measuringPoint="measuringPoint"
      @close="mpDrawerClose"
      @refresh="getDevices"
    />
    <DevicesVolume
      v-if="volumeDrawer"
      :visible="volumeDrawer"
      :meter="meter"
      @close="volumeDrawerClose"
    />
    <DevicesCreateGroup
      v-if="groupDrawer"
      :visible="groupDrawer"
      @close="groupDrawerClose"
      :preloadedDevices="checked"
    />

    <DevicesCreate v-if="deviceDrawer" :visible="deviceDrawer" @close="deviceDrawerClose" />

    <!-- File upload input -->
    <input
      ref="fileInput"
      class="hidden"
      type="file"
      name="fileArray"
      :accept="geoUpdateSupportedFiles.text"
      @change="handleBatchGeoUpdate($event)"
    />

    <!-- Mobile filters -->
    <transition v-if="showFilterModal" name="grow">
      <base-modal @close="showFilterModal = false" head="filters" bg="white" full-width>
        <DevicesFilter :mobile="true" ref="mobileFilter" />
        <!-- Action buttons -->
        <div class="pt-4 mt-6 border-t flex flex-col justify-end w-full">
          <div class="flex flex-col">
            <div class="flex space-x-4">
              <base-button
                class="w-1/2"
                @click="
                  $refs.mobileFilter.reset()
                  showFilterModal = false
                "
                action="clear"
                context="secondary"
              >
                <base-icon icon="close" />
              </base-button>
              <base-button
                class="w-1/2"
                @click="
                  $refs.mobileFilter.submit()
                  showFilterModal = false
                "
                action="submit"
              >
                <base-icon icon="filters" />
              </base-button>
            </div>
          </div>
        </div>
      </base-modal>
    </transition>
  </div>
</template>

<script>
import isEmpty from 'lodash-es/isEmpty'
import {
  BaseAlert,
  BaseModal,
  BaseIcon,
  BaseButton,
  BaseButtonIcon,
  BaseDropdown2,
  BaseCheck,
  BaseSelect,
} from '@/components/base'
import Grid from '@/components/shared/Grid.vue'
import DevicesFilter from './DevicesFilter.vue'
import Popover from 'ant-design-vue/lib/popover'
import 'ant-design-vue/lib/popover/style/index.css'
import DevicesMesuringPoint from './DevicesMeasuringPoint.vue'
import DevicesVolume from './DevicesVolume.vue'
import DevicesCreate from './DevicesCreate.vue'
import DevicesCreateGroup from './DevicesCreateGroup.vue'
import fileExport from '@/state/helpers/fileExport.js'

export default {
  mounted() {
    if (this.$route.params.createDevice) {
      this.deviceDrawer = true
    }
  },
  components: {
    Grid,
    DevicesFilter,
    BaseAlert,
    BaseIcon,
    BaseButton,
    BaseDropdown2,
    BaseButtonIcon,
    BaseCheck,
    BaseModal,
    BaseSelect,
    'a-popover': Popover,
    DevicesMesuringPoint,
    DevicesVolume,
    DevicesCreate,
    DevicesCreateGroup,
  },

  data() {
    return {
      pendingRequest: false,
      pendingBatchGeoRequest: false,
      addToGroup: {
        showComponent: false,
        showForm: false,
        selectedGroupUuid: '',
      },
      showFilterModal: false,
      deviceGroups: [],
      devices: {},
      checked: [],
      clickedAlert: '',
      clickedKey: '',
      keysLoading: '',
      keys: {},
      keysList: {},
      mpDrawer: false,
      measuringPoint: {},
      volumeDrawer: false,
      deviceDrawer: false,
      meter: {},
      groupDrawer: false,
      checkedCompany: '',
      headers: {
        check: {
          label: '',
          className: 'w-16',
        },
        meter__content_type__model: {
          label: 'device',
          order: true,
          className: 'w-1/12 max-w-fit',
        },
        measuring_point__name: {
          label: 'measuring_point',
          order: true,
          className: ' w-2/12 ',
        },
        last_communication_device__content_type__model: {
          label: 'com_module',
          order: true,
          className: 'min-w-max w-2/12',
        },
        company: {
          label: 'company',
          order: true,
          className: '',
        },
        groups: {
          label: 'groups',
        },
        value: {
          label: 'value',
          order: true,
          className: '',
        },
        alerts: {
          label: 'alerts',
          className: 'w-1/12 ',
        },
        last_entry: {
          label: 'last_readout',
          order: true,
          className: '',
        },
      },
    }
  },
  computed: {
    count() {
      return this.devices && this.devices.count ? this.devices.count : 0
    },
    next() {
      return this.devices && this.devices.next ? this.devices.next : null
    },
    previous() {
      return this.devices && this.devices.previous ? this.devices.previous : null
    },
    results() {
      return this.devices && this.devices.results ? this.devices.results : []
    },
    sameCompany() {
      return this.results.every(item => item.company === this.devices.results[0].company)
    },
    showLoadAll() {
      const {limit, offset, ordering, ...params} = this.$route.query
      return !isEmpty(params) && this.results.length < this.count
    },
    checkAll: {
      get: function () {
        return (
          this.results.length > 0 &&
          this.checked.length === this.results.length &&
          this.checked[0] === this.results[0]
        )
      },
      set: function (value) {
        const selectAll = this.results.reduce((acc, device) => [...acc, device], [])
        this.checked = value ? [...selectAll] : []
      },
    },
    geoUpdateSupportedFiles() {
      const supportedFormats = {
        csv: 'text/csv,application/vnd.ms-excel',
      }

      let text = ''
      let formats = []
      let types = []
      Object.entries(supportedFormats).forEach(([fileFormat, fileTypes], index) => {
        text += `${index > 0 ? ',' : ''}.${fileFormat}`
        formats.push(fileFormat)
        types.push(...fileTypes.split(','))
      })
      return {text, formats, types}
    },
  },
  watch: {
    checked(n, o) {
      if (this.checked.length) {
        this.checkedCompany = this.checked[0].company
      } else {
        this.checkedCompany = ''
      }
    },
  },
  methods: {
    async getDevices() {
      const devices = await this.$store.dispatch('devices/getDevices')
      devices.results = devices.results.map(item => {
        return {...item, state: {...item.state, alertCount: this.alertsCount(item)}}
      })
      this.devices = devices
      this.checkedCompany = ''
      this.checked = []
    },
    async deviceKeys(item) {
      if (this.keysLoading) {
        return
      }
      this.keys = []
      if (this.keysList.hasOwnProperty(item.uuid)) {
        this.keys = this.keysList[item.uuid]
        return
      }
      this.keysLoading = item.uuid

      //  this.keys = await this.$store.dispatch('keys/getDeviceKeys', item.comd_serial)
      const keys = await this.$store.dispatch('keys/getDeviceKeys', item.comd_uuid)

      this.keys = keys.data
      this.keysList[item.uuid] = keys.data
      this.keysLoading = ''
    },
    alertsCount(device) {
      const deviceAlerts = this.deviceAlerts(device)
      return Object.values(deviceAlerts).filter(item => item).length
    },
    deviceAlerts(device) {
      const {volume, last_entry, battery_lifetime, alertCount, ...deviceAlerts} = device.state
      return deviceAlerts
    },
    alertText(key) {
      return this.$t(`mygwf.components.User.ReportState.${key}`)
    },
    alertIcon(bool) {
      return bool ? 'info' : 'success'
    },
    disabledCheck(item) {
      if (!item.company || (this.checkedCompany !== '' && item.company !== this.checkedCompany)) {
        return true
      }
      return false
    },
    text(field) {
      return field.split('_').join(' ')
    },
    mpClick(item) {
      // this.measuringPoint = {
      //   lat: item.lat,
      //   lng: item.lng,
      //   id: item.mp_id,
      //   meter_serial: item.meter_serial,
      //   name: item.mp_name,
      //   uuid: item.uuid,
      //   value: item.state.volume,
      //   comd_type: item.comd_type,
      //   comd_serial: item.comd_serial,
      // }
      this.measuringPoint = {...item}
      this.mpDrawer = true
    },
    // measuringPointUpdate(item) {
    //   let updatedDevice = this.devices.results.filter(device => device.uuid === item.uuid)[0]
    //   updatedDevice.lat = item.lat
    //   updatedDevice.lng = item.lng
    //   updatedDevice.mp_name = item.name
    // },
    mpDrawerClose(changes) {
      this.measuringPoint = {}
      this.mpDrawer = false
      // if (changes) {
      //   this.getDevices()
      // }
    },
    volumeClick(item) {
      this.meter = {
        uuid: item.uuid,
        meter_type: item.meter_type,
        meter_serial: item.meter_serial,
        comd_serial: item.comd_serial,
      }
      this.volumeDrawer = true
    },
    volumeDrawerClose() {
      this.meter = {}
      this.volumeDrawer = false
    },
    createDevice() {
      this.deviceDrawer = true
    },
    deviceDrawerClose(changes) {
      this.deviceDrawer = false
      if (changes) {
        this.getDevices()
      }
    },
    createGroup() {
      this.$store.dispatch('deviceGroups/setEditedGroup', {mode: 'creation'})
      this.groupDrawer = true
    },
    groupDrawerClose() {
      this.getDevices()
      this.groupDrawer = false
      this.checked = []
    },
    groupClick(group) {
      let uuid = group.uuid
      this.$router.push({path: `/device-groups/${uuid}`})
    },
    onVisibleChange() {
      //onClose
      if (this.addToGroup.showComponent && this.checked.length === 0) {
        this.addToGroup.showComponent = false
      }
      this.addToGroup.showForm = false
      this.addToGroup.selectedGroupUuid = ''
    },

    downloadTemplate() {
      if (this.pendingRequest) {
        return
      }

      this.pendingRequest = true

      this.$store
        .dispatch(`devices/getGeolocationTemplate`)
        .then(file => {
          fileExport(file, 'geocode_template.csv')
        })
        .then(() => {
          this.$store.dispatch('app/notification', {
            type: 'success',
            group: 'app',
            text: `download`,
          })
        })
        .catch(err => console.log(err))
        .finally(() => (this.pendingRequest = false))
    },

    async handleBatchGeoUpdate(e) {
      if (this.pendingBatchGeoRequest) {
        return
      }

      const generateTaskReport = taskResult => {
        const report = {
          file: null,
          fileName: null,
          text: null,
        }
        let reportText = ''
        reportText +=
          `Batch geolocation update completed with:\n` +
          `\t- ${taskResult.detail.measuring_points_updated} measuring points updated\n` +
          `\t- ${taskResult.detail.warnings.length} warnings\n` +
          `\t- ${taskResult.detail.errors.length} errors`
        reportText += '\n\n----------------------------\n\nWarnings\n\n'
        taskResult.detail.warnings.forEach(
          (e, i) => (reportText += `[${i + 1}] ${e.meter_serial}: ${e.reason}`)
        )
        reportText += '\n\n----------------------------\n\nErrors\n\n'
        taskResult.detail.errors.forEach(
          (e, i) => (reportText += `[${i + 1}] ${e.meter_serial}: ${e.reason}`)
        )

        report.file = reportText
        report.fileName = `batch-geolocation-update-report-${new Date()
          .toISOString()
          .substring(0, 10)}.txt`
        report.text = reportText
        return report
      }

      // Task polling function
      const pollBatchGeoUpdateAsyncTask = async task => {
        if (!task) {
          return
        }

        try {
          const status = await this.$store.dispatch(
            'asyncTasksTracker/pollAsyncTaskStatus',
            task.taskId
          )
          if (status.state === 'FAILURE') {
            throw status
          }

          if (status.state === 'SUCCESS') {
            // No warning / errors
            if (!status?.detail.warnings.length && !status?.detail.errors.length) {
              await this.$store.dispatch('asyncTasksTracker/updateTaskStatus', {
                id: task.asyncId,
                status: 'complete',
              })
              await this.$store.dispatch('asyncTasksTracker/updateTaskTitle', {
                id: task.asyncId,
                title: 'Devices geolocation updated',
              })
              // Warning and/or errors
            } else {
              const report = generateTaskReport(status)
              await this.$store.dispatch('asyncTasksTracker/updateTaskReport', {
                id: task.asyncId,
                report,
              })
              await this.$store.dispatch('asyncTasksTracker/updateTaskStatus', {
                id: task.asyncId,
                status: 'warning',
              })
              await this.$store.dispatch('asyncTasksTracker/updateTaskTitle', {
                id: task.asyncId,
                title: 'Task completed with warnings',
              })
            }

            clearInterval(task.intervalId)
            await this.getDevices()
          }
        } catch (e) {
          console.log(e)
          await this.$store.dispatch('asyncTasksTracker/updateTaskStatus', {
            id: task.asyncId,
            status: 'failed',
          })
          clearInterval(task.intervalId)
        }
      }

      // Handle geo update
      try {
        this.pendingBatchGeoRequest = true
        e.preventDefault()
        const {types: supportedTypes, formats: supportedFormats} = this.geoUpdateSupportedFiles
        const files = e.target.files

        // Return if no files are selected
        if (!files.length) {
          return
        }
        const fileObject = files[0]
        const fileFormat = fileObject.name.split('.').slice(-1)[0]

        // Return if provided file type or format is not supported
        if (
          !supportedTypes.includes(fileObject.type) ||
          !supportedFormats.includes(fileFormat.toLowerCase())
        ) {
          return
        }

        // Start geolocation update
        let task = this.$store.getters['asyncTasksTracker/getNewTask']
        task = {
          ...task,
          taskId: null,
          asyncId: null,
          intervalId: null,
          status: 'pending',
          title: 'Updating devices geolocation',
        }
        task.taskId = await this.$store.dispatch('devices/updateGeolocation', fileObject)
        task.asyncId = await this.$store.dispatch('asyncTasksTracker/addTask', task)

        // Poll for task status
        task.intervalId = setInterval(pollBatchGeoUpdateAsyncTask, 1000, task)
      } catch (e) {
        console.log(e)
        if (e?.detail?.[0]?.toLowerCase() === 'malformed csv') {
          await this.$store.dispatch('app/notification', {
            type: 'error',
            group: 'app',
            text: `malformed_csv`,
          })
        }
      } finally {
        this.pendingBatchGeoRequest = false
        this.$refs.fileInput.value = ''
      }
    },

    async fetchDeviceGroupsByCompanyId() {
      try {
        const companyName = this.checkedCompany || ''
        const company = this.$store.getters['companies/getByName'](companyName)
        const payload = company ? {company: company.id} : {}
        const groups = await this.$store.dispatch('deviceGroups/getDeviceGroupsBy', payload)
        this.deviceGroups = groups.results
      } catch (e) {
        console.log(e)
      }
    },

    async onAddToExistingGroup() {
      if (this.pendingRequest) {
        return
      }

      try {
        this.pendingRequest = true
        const selectedGroup = this.$store.getters['deviceGroups/getDeviceGroupByUuid'](
          this.addToGroup.selectedGroupUuid
        )
        if (!selectedGroup) {
          return
        }

        // Filter out devices that may exist in the group
        const deviceUuids = this.checked
          .map(e => e.uuid)
          .filter(e => !selectedGroup.installations.includes(e))

        // Add devices to group
        await this.$store.dispatch('deviceGroups/addDevices', {
          group_uuid: selectedGroup.uuid,
          device_uuids: deviceUuids,
        })
        this.checked = []
        this.addToGroup.selectedGroupUuid = ''
        await this.$store.dispatch('app/notification', {
          type: 'success',
          group: 'app',
          text: 'added',
        })
        await this.getDevices()
      } catch (e) {
        console.log(e)
      } finally {
        this.pendingRequest = false
        this.addToGroup.showForm = false
        this.addToGroup.showComponent = false
      }
    },
  },
}
</script>

<style>
.no-padding .ant-popover-inner-content {
  padding: 0;
}
</style>
